import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Box,
  Divider,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ptLocale from 'date-fns/locale/pt';
import enLocale from 'date-fns/locale/en-US';
import ModalTemplate from './ModalTemplate';
import { deviceAPI } from '../../API/deviceAPI';
import usePermissions from '../../Context/Hooks/usePermissions';
import { PERMISSIONS } from '../../Constants/permissions';

/**
 * EditDeviceModal - Modular modal for updating device metadata.
 * Editable fields: name, serialNumber, location, installationDate (MUI DatePicker).
 * Read-only hardware & network identifiers: model, MAC, IP, store, client.
 */
const EditDeviceModal = ({
  open,
  device,
  storeName,
  clientName,
  onClose,
  onSuccess,
}) => {
  const { t, i18n } = useTranslation(['devices', 'common']);
  const { can } = usePermissions();
  const canManage = can(PERMISSIONS.DEVICES_MANAGE);
  const currentLang = (i18n.language || 'pt').split('-')[0];
  const dateFnsLocale = currentLang === 'en' ? enLocale : ptLocale;

  const [form, setForm] = useState({
    name: '',
    serialNumber: '',
    location: '',
    installationDate: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (device && open) {
      let initialDate = null;
      if (device.installationDate) {
        const d = new Date(device.installationDate);
        if (!isNaN(d.getTime())) {
          initialDate = d;
        }
      }

      setForm({
        name: device.name || '',
        serialNumber: device.serialNumber || '',
        location: device.location || '',
        installationDate: initialDate,
      });
    }
  }, [device, open]);

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!device?.id) return;
    const trimmedName = form.name.trim();
    if (!trimmedName) {
      toast.error(t('feedback.nameRequired', 'O nome do dispositivo é obrigatório.'));
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        name: trimmedName,
        serialNumber: form.serialNumber.trim() || null,
        location: form.location.trim() || null,
        installationDate: form.installationDate instanceof Date && !isNaN(form.installationDate.getTime())
          ? form.installationDate.toISOString()
          : null,
      };

      const res = await deviceAPI.update(device.id, payload);
      if (res && !res.error) {
        toast.success(t('feedback.deviceUpdated', 'Dispositivo atualizado com sucesso.'));
        if (onSuccess) onSuccess(res.value || { ...device, ...payload });
        onClose();
      } else {
        toast.error(res?.description || t('feedback.errorUpdatingDevice', 'Erro ao atualizar o dispositivo.'));
      }
    } catch {
      toast.error(t('feedback.errorUpdatingDevice', 'Erro ao atualizar o dispositivo.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!device || !canManage) return null;

  return (
    <ModalTemplate
      open={Boolean(open)}
      onClose={onClose}
      title={t('menu.edit', 'Editar dispositivo')}
      icon={<EditIcon color="primary" />}
      size="sm"
      isSubmitting={isSubmitting}
      onCancel={onClose}
      onConfirm={handleSave}
      canConfirm={Boolean(form.name.trim()) && !isSubmitting}
      content={
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* Editable Fields Section */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600 }}>
              {t('modal.editableSectionTitle', 'Informação Geral')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label={t('fields.name', 'Nome do dispositivo')}
                  value={form.name}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                  fullWidth
                  variant="outlined"
                  size="small"
                  required
                  error={!form.name.trim()}
                  helperText={!form.name.trim() ? t('feedback.nameRequired', 'O nome do dispositivo é obrigatório.') : ''}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('fields.serialNumber', 'Número de série')}
                  value={form.serialNumber}
                  onChange={(e) => handleFieldChange('serialNumber', e.target.value)}
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder={t('fields.serialNumberPlaceholder', 'Ex: SN-12345678')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('fields.location', 'Localização')}
                  value={form.location}
                  onChange={(e) => handleFieldChange('location', e.target.value)}
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder={t('fields.locationPlaceholder', 'Ex: Entrada Piso 0')}
                />
              </Grid>
              <Grid item xs={12}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateFnsLocale}>
                  <DatePicker
                    label={t('fields.installationDate', 'Data de instalação')}
                    value={form.installationDate}
                    onChange={(newDate) => handleFieldChange('installationDate', newDate)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        variant="outlined"
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* Read-Only Hardware / Association Information */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600 }}>
              {t('modal.readOnlySectionTitle', 'Identificação & Rede (Só de leitura)')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('detail.infoCard.store', 'Loja')}
                  value={storeName || device.storeName || '—'}
                  fullWidth
                  variant="outlined"
                  size="small"
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('detail.infoCard.client', 'Cliente')}
                  value={clientName || device.clientName || '—'}
                  fullWidth
                  variant="outlined"
                  size="small"
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('detail.infoCard.model', 'Modelo')}
                  value={device.model || '—'}
                  fullWidth
                  variant="outlined"
                  size="small"
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('detail.infoCard.macAddress', 'Endereço MAC')}
                  value={device.macAddress || '—'}
                  fullWidth
                  variant="outlined"
                  size="small"
                  disabled
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t('detail.infoCard.ipAddress', 'Endereço IP')}
                  value={device.ipAddress || '—'}
                  fullWidth
                  variant="outlined"
                  size="small"
                  disabled
                />
              </Grid>
            </Grid>
          </Box>
        </Box>
      }
    />
  );
};

EditDeviceModal.propTypes = {
  open: PropTypes.bool.isRequired,
  device: PropTypes.object,
  storeName: PropTypes.string,
  clientName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default EditDeviceModal;
